import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import {
  selectFrontendColorCorrectionProcessDarkImagesOverride,
  selectFrontendColorCorrectionResult,
} from './imageSlice';
import { MAX_BACKEND_CLIP_LIMIT } from './openCV/hooks/useFrontendColorCorrection';
/**
 * ColorCorrectionErrorIndicator component displays an error indicator based on the frontend color correction result.
 *
 * Warning Levels:
 * - Clip Limit < 10: Normal brightness; closely matches backend processing.
 * - 10 < Clip Limit < 15: Slightly dark but usable for frontend work; warnings appear only in QA mode. Does not match backend! May have slight distortion / pixelation artifacts.
 * - Clip Limit > 15: Too dark. Customers see a warning advising a Flash check by Bloomfield. Frontend color correction is turned off to show the issue.
 *
 * @returns {JSX.Element | null} The error indicator component or null if no error.
 */
const ColorCorrectionErrorIndicator: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const frontendColorCorrectionResult = useAppSelector(
    selectFrontendColorCorrectionResult
  );
  const { Diagnostics } = useFeatureFlags();
  const processDarkImagesOverride = useAppSelector(
    selectFrontendColorCorrectionProcessDarkImagesOverride
  );

  const message = useMemo(() => {
    if (processDarkImagesOverride) {
      return t('Forced color correction is on in image settings.');
    }

    if (frontendColorCorrectionResult.error) {
      return t(
        'Dark image detected. Increase BloomGo brightness and contact support if the issue persists.'
      );
    }

    return `${t('Color correction clip limit of ')}${
      frontendColorCorrectionResult.finalClipLimit
    }${t(
      ' exceeds backend limit of '
    )}${MAX_BACKEND_CLIP_LIMIT}.  Displayed images may differ from AI-processed images.`;
  }, [
    t,
    frontendColorCorrectionResult.error,
    frontendColorCorrectionResult.finalClipLimit,
    processDarkImagesOverride,
  ]);

  if (
    !processDarkImagesOverride &&
    ((Diagnostics &&
      frontendColorCorrectionResult.finalClipLimit > MAX_BACKEND_CLIP_LIMIT) ||
      frontendColorCorrectionResult.error) === false
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: '1rem',
        bottom: '1rem',
        zIndex: 100,
      }}
    >
      <Tooltip
        title={message}
        placement='top'
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: theme.colors.common.darkGrey,
              color: theme.palette.common.white,
              fontSize: '0.875rem',
              p: '0.5rem',
            },
          },
        }}
      >
        <Box
          sx={{
            bgcolor: theme.colors.common.white,
            p: '0.375rem',
            borderRadius: '0.25rem',
            position: 'relative',
            border: processDarkImagesOverride
              ? `2px solid ${theme.colors.darkTheme.red} `
              : 'none',
          }}
        >
          <DiagnosticFeature>
            <Box
              sx={{
                width: '6px',
                height: '6px',
                position: 'absolute',
                top: '3px',
                right: '3px',
                bgcolor: theme.colors.darkTheme.red,
                borderRadius: '50%',
                display:
                  frontendColorCorrectionResult.finalClipLimit >
                  MAX_BACKEND_CLIP_LIMIT
                    ? 'block'
                    : 'none',
              }}
            />
          </DiagnosticFeature>
          <Typography
            sx={{
              color: theme.colors.common.black,
              fontSize: '0.75rem',
              fontWeight: 700,
              fontFamily: 'inter, arial, sans-serif',
            }}
          >
            {processDarkImagesOverride ? t('Forced Color On') : t('Dark Image')}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ColorCorrectionErrorIndicator;
